import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'

/**
 * Yadviga's pose for a loading state — chosen by the calling screen so the
 * witch is always doing the right thing for the reading we're waiting on:
 *  • `tarot`    — holding a tarot card  (daily card, spreads)
 *  • `mystical` — reading the chart     (natal, horoscope, compat)
 */
type Pose = 'tarot' | 'mystical'

const POSES: Record<Pose, string> = {
  tarot: '/characters/yadviga_shuffling.webp',
  mystical: '/characters/yadviga_mystical_reading.webp',
}

interface Props {
  label?: string
  pose?: Pose
  /** When true, hides the Yadviga illustration — useful for tight inline spots. */
  compact?: boolean
}

export function LoadingDots({ label, pose = 'mystical', compact = false }: Props) {
  const { t } = useTranslation()
  const poseUrl = POSES[pose]
  // Flavour text rotates every few seconds so the wait feels alive — pulled
  // from i18n so RU / EN (and any future locale) stay in sync.
  const messages = t(`loading.${pose}`, { returnObjects: true }) as string[]
  const [messageIdx, setMessageIdx] = useState(0)

  useEffect(() => {
    if (label) return // explicit label freezes the rotation
    const id = setInterval(() => {
      setMessageIdx((i) => (i + 1) % messages.length)
    }, 3200)
    return () => clearInterval(id)
  }, [label, messages.length])

  const message = label ?? messages[messageIdx % messages.length]!

  if (compact) {
    return (
      <div className="flex items-center gap-1.5 py-2">
        <span className="block h-2 w-2 animate-pulse rounded-full bg-sage-500 [animation-delay:0ms]" />
        <span className="block h-2 w-2 animate-pulse rounded-full bg-sage-500 [animation-delay:200ms]" />
        <span className="block h-2 w-2 animate-pulse rounded-full bg-sage-500 [animation-delay:400ms]" />
        <span className="ml-2 text-xs text-sage-600">{message}</span>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-4 py-6 text-sage-700">
      <motion.img
        src={poseUrl}
        alt=""
        aria-hidden="true"
        className="h-auto w-full max-w-[280px] select-none drop-shadow-[0_8px_18px_rgba(52,68,43,0.2)]"
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
        draggable={false}
      />
      <div className="flex gap-1.5">
        <span className="block h-2.5 w-2.5 animate-pulse rounded-full bg-sage-500 [animation-delay:0ms]" />
        <span className="block h-2.5 w-2.5 animate-pulse rounded-full bg-sage-500 [animation-delay:200ms]" />
        <span className="block h-2.5 w-2.5 animate-pulse rounded-full bg-sage-500 [animation-delay:400ms]" />
      </div>
      <AnimatePresence mode="wait">
        <motion.p
          key={message}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.35 }}
          className="min-h-[1.25rem] text-center text-sm text-sage-600"
        >
          {message}
        </motion.p>
      </AnimatePresence>
    </div>
  )
}
